import { clearCartBoxes, clearOrderBuilderState } from '../order/cartStorage'
import { clearCartAddOns } from '../order/addOnStorage'
import { clearCheckoutDraft } from './checkoutStorage'
import { useEffect, useState, type ReactNode } from 'react'
import { stagingQaRuntimeAllowed } from '../config/runtimeEnvironment'
import { AnalyticsConsentCard } from '../analytics/AnalyticsConsent'
import { subscribeEffectiveAnalyticsConsent, subscribeEffectiveMarketingConsent } from '../analytics/analytics'
import { trackPaidPurchaseEvents } from '../analytics/ecommerceTracking'
import { recordBrowserPurchaseDiagnostic, fetchVolleyStatus, finishVolleySession, type VolleyStatus } from './checkoutApi'
import { HomepageV3Footer, HomepageV3Header } from '../components/HomepageV3Chrome'
import { businessData, getPackLabel, setWeeklyOrderProducts } from '../config/businessData'
import { fetchPublishedMenu } from '../config/liveMenu'
import { fetchPricingDelivery, installPricingDelivery } from '../config/livePricingDelivery'
import type { CartBox, CartSelection } from '../order/cartStorage'
import {
  CONFIRMATION_SNAPSHOT_PREFIX,
  resolveConfirmationState,
  type ConfirmationMethod,
  type ConfirmationSnapshot,
} from './confirmationState'
import '../styles/checkout-confirmation.css'
import '../styles/addons-v1.css'

type IconName = 'check' | 'hourglass' | 'receipt' | 'calendar' | 'bank' | 'alert' | 'heart' | 'chef' | 'truck' | 'box' | 'pin' | 'user' | 'leaf' | 'home' | 'copy'

function formatCents(cents: number) {
  return new Intl.NumberFormat('en-NZ', {
    style: 'currency',
    currency: businessData.currency,
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100)
}

function formatPlacedAt(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return { date: '', time: '' }
  return {
    date: new Intl.DateTimeFormat('en-NZ', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date),
    time: new Intl.DateTimeFormat('en-NZ', {
      hour: 'numeric',
      minute: '2-digit',
    }).format(date),
  }
}

function Icon({ name }: { name: IconName }) {
  const content: Record<IconName, ReactNode> = {
    check: <path d="m8 16 5 5L25 9" />,
    hourglass: <><path d="M10 5h12M10 27h12M11 5c0 6 2 8 5 11-3 3-5 5-5 11M21 5c0 6-2 8-5 11 3 3 5 5 5 11" /></>,
    receipt: <><path d="M9 5h14v22l-3-2-4 2-4-2-3 2V5Z" /><path d="M12 10h8M12 14h8M12 18h5" /></>,
    calendar: <><rect x="6" y="8" width="20" height="18" rx="3" /><path d="M10 5v6M22 5v6M6 13h20" /></>,
    bank: <><path d="m4 12 12-7 12 7H4Z" /><path d="M7 14h18M9 14v10M14 14v10M19 14v10M24 14v10M6 26h20" /></>,
    alert: <><circle cx="16" cy="16" r="11" /><path d="M16 10v8M16 22h.01" /></>,
    heart: <path d="M16 27S6 21 6 13a5 5 0 0 1 9-3 5 5 0 0 1 9 3c0 8-8 14-8 14Z" />,
    chef: <><path d="M10 15a6 6 0 0 1 3-11 6 6 0 0 1 6 0 6 6 0 0 1 3 11v10H10V15Z" /><path d="M10 19h12M13 25v3h6v-3" /></>,
    truck: <><path d="M4 9h15v13H4V9Z" /><path d="M19 14h5l4 5v3h-9v-8Z" /><circle cx="10" cy="24" r="2" /><circle cx="23" cy="24" r="2" /></>,
    box: <><path d="m16 4 11 6-11 6-11-6 11-6Z" /><path d="m5 10 11 6v12L5 22V10ZM27 10l-11 6v12l11-6V10Z" /></>,
    pin: <><path d="M16 28s8-8 8-15a8 8 0 1 0-16 0c0 7 8 15 8 15Z" /><circle cx="16" cy="13" r="2.5" /></>,
    user: <><circle cx="16" cy="10" r="5" /><path d="M7 28c1-7 4-10 9-10s8 3 9 10" /></>,
    leaf: <><path d="M27 6C15 7 8 13 7 25c9 1 17-4 20-19Z" /><path d="M9 23c5-5 9-8 15-12" /></>,
    home: <><path d="m5 15 11-9 11 9" /><path d="M8 13v14h16V13M13 27v-8h6v8" /></>,
    copy: <><rect x="10" y="9" width="13" height="16" rx="2" /><path d="M8 21H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2" /></>,
  }

  return (
    <svg viewBox="0 0 32 32" aria-hidden="true">
      {content[name]}
    </svg>
  )
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1600)
    } catch {
      setCopied(false)
    }
  }

  return (
    <button className="checkout-confirmation__copy" type="button" onClick={copy} aria-label={`Copy ${label}`}>
      <Icon name="copy" />
      <span>{copied ? 'Copied' : 'Copy'}</span>
    </button>
  )
}

function readConfirmationSnapshot(orderId: string): unknown {
  if (!orderId || typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(`${CONFIRMATION_SNAPSHOT_PREFIX}${orderId}`)
    if (!raw) return null
    return JSON.parse(raw) as unknown
  } catch {
    return null
  }
}

function buildQaPreview(method: ConfirmationMethod): ConfirmationSnapshot | null {
  if (typeof window === 'undefined' || !stagingQaRuntimeAllowed(window.location.hostname)) return null
  const pack = businessData.packs.find((candidate) => candidate.quantity === 6 && candidate.available) ?? businessData.packs[0]
  if (!pack) return null
  const products = businessData.products.filter((product) => product.orderable).slice(0, pack.quantity)
  if (products.length === 0) return null

  const selections: CartSelection[] = products.map((product) => ({
    productId: product.id,
    productName: product.name,
    category: product.type,
    image: product.image,
    presentation: 'confirmation-qa',
    quantity: 1,
  }))

  while (selections.reduce((sum, item) => sum + item.quantity, 0) < pack.quantity) {
    selections[0].quantity += 1
  }

  const subtotalCents = Math.round(pack.price * 100)
  const deliveryFeeCents = 499
  return {
    version: 1,
    orderId: method === 'card' ? 'SG900' : 'SG901',
    method,
    paymentStatus: method === 'card' ? 'paid' : 'pending',
    amountCents: subtotalCents + deliveryFeeCents,
    subtotalCents,
    discountCents: 0,
    deliveryFeeCents,
    placedAt: new Date().toISOString(),
    boxes: [{
      id: 'confirmation-qa-box',
      quantity: pack.quantity,
      price: pack.price,
      currency: 'NZD',
      selections,
      createdAt: new Date().toISOString(),
    }],
    contact: {
      fullName: 'QA Preview',
      phone: '021 000 0000',
      email: 'qa-preview@sugargremlin.co.nz',
    },
    delivery: {
      address: '123 QA Street',
      suburb: 'Christchurch',
      postcode: '8011',
      deliveryDay: 'saturday',
      deliveryNotes: '',
    },
  }
}

function physicalSelections(box: CartBox): CartSelection[] {
  return box.selections.flatMap((selection) => (
    Array.from({ length: selection.quantity }, () => selection)
  ))
}

function ProgressStep({ icon, title, body }: { icon: IconName; title: string; body: string }) {
  return (
    <div className="checkout-confirmation__progress-step" style={{ justifyContent: 'center' }}>
      <span className="checkout-confirmation__progress-icon"><Icon name={icon} /></span>
      <span>
        <strong>{title}</strong>
        <small>{body}</small>
      </span>
    </div>
  )
}

function OrderPanel({ snapshot, totalCents, method }: {
  snapshot: Pick<ConfirmationSnapshot, 'boxes' | 'addons' | 'contact' | 'delivery' | 'subtotalCents' | 'discountCents' | 'deliveryFeeCents'> | null
  totalCents: number | null
  method: ConfirmationMethod | 'volley'
}) {
  const legacySubtotalCents = snapshot
    ? snapshot.boxes.reduce((sum, box) => sum + Math.round(box.price * 100), 0)
      + (snapshot.addons ?? []).reduce((sum, item) => sum + item.unitPriceCents * item.quantity, 0)
    : null
  const subtotalCents = snapshot && Number.isInteger(snapshot.subtotalCents)
    ? Number(snapshot.subtotalCents)
    : legacySubtotalCents
  const discountCents = snapshot && Number.isInteger(snapshot.discountCents)
    ? Number(snapshot.discountCents)
    : 0
  const deliveryFeeCents = snapshot && Number.isInteger(snapshot.deliveryFeeCents)
    ? Number(snapshot.deliveryFeeCents)
    : subtotalCents !== null && totalCents !== null
      ? Math.max(0, totalCents - subtotalCents)
      : null
  const deliveryDay = snapshot
    ? businessData.delivery.days.find((day) => day.id === snapshot.delivery.deliveryDay)
    : null

  return (
    <aside className="checkout-confirmation__order-panel" aria-label="Order summary">
      <header className="checkout-confirmation__order-header">
        <h2>Your Order</h2>
        <span className={`checkout-confirmation__status checkout-confirmation__status--${method === 'bank-transfer' ? 'pending' : 'paid'}`}>
          <Icon name={method === 'bank-transfer' ? 'hourglass' : 'check'} />
          {method === 'bank-transfer' ? 'Awaiting Payment' : 'Paid'}
        </span>
      </header>

      {snapshot ? (
        <div className="checkout-confirmation__order-boxes">
          {snapshot.boxes.map((box) => (
            <section className="checkout-confirmation__order-box" key={box.id}>
              <div className="checkout-confirmation__order-box-heading">
                <span><Icon name="box" /></span>
                <strong>{getPackLabel(box.quantity)}</strong>
                <b>{formatCents(Math.round(box.price * 100))}</b>
              </div>
              <div className="checkout-confirmation__products">
                {physicalSelections(box).map((selection, index) => (
                  <div className="checkout-confirmation__product" key={`${box.id}-${selection.productId}-${index}`}>
                    <span><img src={selection.image} alt="" /></span>
                    <small>{selection.productName}</small>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : null}

      {snapshot?.addons?.length ? (
        <section className="checkout-confirmation__addons" aria-label="Add-ons">
          <strong>ADD-ONS</strong>
          {snapshot.addons.map((item) => (
            <div className="checkout-confirmation__addon-line" key={item.id}>
              <span>{item.name} — {item.packLabel} × {item.quantity}</span>
              <strong>{formatCents(item.unitPriceCents * item.quantity)}</strong>
            </div>
          ))}
        </section>
      ) : null}

      {snapshot ? (
        <div className="checkout-confirmation__order-details">
          <div>
            <span className="checkout-confirmation__detail-icon"><Icon name="calendar" /></span>
            <p><strong>Delivery day</strong>{deliveryDay?.label ?? snapshot.delivery.deliveryDay}<small>{deliveryDay?.window ?? businessData.delivery.deliveryWindow}</small></p>
          </div>
          <div>
            <span className="checkout-confirmation__detail-icon"><Icon name="pin" /></span>
            <p><strong>Delivery address</strong>{snapshot.delivery.address}<small>{[snapshot.delivery.suburb, snapshot.delivery.postcode].filter(Boolean).join(' ')}</small></p>
          </div>
          <div>
            <span className="checkout-confirmation__detail-icon"><Icon name="user" /></span>
            <p><strong>Contact</strong>{snapshot.contact.fullName}<small>{snapshot.contact.phone}<br />{snapshot.contact.email}</small></p>
          </div>
        </div>
      ) : null}

      {totalCents !== null ? (
        <dl className="checkout-confirmation__totals">
          {subtotalCents !== null ? <div><dt>Subtotal</dt><dd>{formatCents(subtotalCents)}</dd></div> : null}
          {discountCents > 0 ? <div><dt>Discount</dt><dd>-{formatCents(discountCents)}</dd></div> : null}
          {deliveryFeeCents !== null ? <div><dt>Delivery</dt><dd>{formatCents(deliveryFeeCents)}</dd></div> : null}
          <div className="checkout-confirmation__total"><dt>Total</dt><dd>{formatCents(totalCents)}</dd></div>
        </dl>
      ) : null}

      {method === 'bank-transfer' ? (
        <div className="checkout-confirmation__fresh-note">
          <span><Icon name="leaf" /></span>
          <p><strong>Fresh-baked to order</strong>We start baking once your payment is received and confirmed.</p>
        </div>
      ) : null}
    </aside>
  )
}

function LegacyCheckoutConfirmationPage() {
  const params = new URLSearchParams(window.location.search)
  const requestedQaPreview = stagingQaRuntimeAllowed(window.location.hostname) ? params.get('qa-preview') : null
  const previewMethod: ConfirmationMethod | null = requestedQaPreview === 'bank-transfer'
    ? 'bank-transfer'
    : requestedQaPreview === 'card'
      ? 'card'
      : null
  const [qaAuthorityState, setQaAuthorityState] = useState<'idle' | 'loading' | 'ready' | 'error'>(() => (
    previewMethod ? 'loading' : 'idle'
  ))

  useEffect(() => {
    if (!previewMethod) {
      setQaAuthorityState('idle')
      return undefined
    }

    let cancelled = false
    setQaAuthorityState('loading')

    Promise.all([fetchPublishedMenu(), fetchPricingDelivery()])
      .then(([menu, pricing]) => {
        if (cancelled) return
        setWeeklyOrderProducts(menu.products)
        installPricingDelivery(pricing)
        setQaAuthorityState('ready')
      })
      .catch(() => {
        if (!cancelled) setQaAuthorityState('error')
      })

    return () => {
      cancelled = true
    }
  }, [previewMethod])

  const qaSnapshot = previewMethod && qaAuthorityState === 'ready' ? buildQaPreview(previewMethod) : null
  const confirmation = resolveConfirmationState(params, readConfirmationSnapshot, qaSnapshot)
  const purchaseSnapshot = confirmation.kind === 'valid' ? confirmation.snapshot : null

  useEffect(() => {
    if (!purchaseSnapshot || purchaseSnapshot.method !== 'card' || purchaseSnapshot.paymentStatus !== 'paid') return undefined

    const sendPurchase = () => {
      const result = trackPaidPurchaseEvents(purchaseSnapshot)
      if (
        purchaseSnapshot.measurementToken
        && (result.metaState === 'queued' || result.metaState === 'not-permitted' || result.metaState === 'not-configured')
      ) {
        void recordBrowserPurchaseDiagnostic(
          purchaseSnapshot.orderId,
          purchaseSnapshot.measurementToken,
          result.metaState,
        )
      }
      return result
    }
    sendPurchase()

    const unsubscribeAnalytics = subscribeEffectiveAnalyticsConsent((consent) => {
      if (consent === 'granted') sendPurchase()
    })
    const unsubscribeMarketing = subscribeEffectiveMarketingConsent((consent) => {
      if (consent === 'granted') sendPurchase()
    })

    return () => {
      unsubscribeAnalytics()
      unsubscribeMarketing()
    }
  }, [purchaseSnapshot?.orderId])

  if (previewMethod && qaAuthorityState === 'loading') {
    return (
      <div className="sg-v3 checkout-confirmation-page">
        <a className="skip-link" href="#main-content">Skip to main content</a>
        <HomepageV3Header cartCount={0} activeHref="/checkout" />
        <main id="main-content" tabIndex={-1}>
          <section className="checkout-confirmation checkout-confirmation--card">
            <div className="checkout-confirmation__layout">
              <article className="checkout-confirmation__primary">
                <header className="checkout-confirmation__hero">
                  <span className="checkout-confirmation__hero-icon"><Icon name="hourglass" /></span>
                  <h1>Loading QA confirmation…</h1>
                  <p>One moment while we load the current staging menu and pricing.</p>
                </header>
              </article>
            </div>
          </section>
        </main>
        <HomepageV3Footer />
        <AnalyticsConsentCard />
      </div>
    )
  }

  if (confirmation.kind !== 'valid') {
    return (
      <div className="sg-v3 checkout-confirmation-page">
        <a className="skip-link" href="#main-content">Skip to main content</a>
        <HomepageV3Header cartCount={0} activeHref="/checkout" />
        <main id="main-content" tabIndex={-1}>
          <section className="checkout-confirmation checkout-confirmation--card">
            <div className="checkout-confirmation__layout">
              <article className="checkout-confirmation__primary">
                <header className="checkout-confirmation__hero">
                  <span className="checkout-confirmation__hero-icon"><Icon name="alert" /></span>
                  <h1>Order confirmation unavailable</h1>
                  <p>We couldn’t verify an order from this confirmation link, so no payment or order status is being shown.</p>
                </header>
                <div className="checkout-confirmation__next">
                  <span><Icon name="heart" /></span>
                  <p><strong>Need help?</strong>If you just completed payment and expected an order confirmation, please contact Sugar Gremlin and we’ll check it for you.</p>
                </div>
                <a className="checkout-confirmation__home" href="/contact">
                  <Icon name="home" />
                  Contact Sugar Gremlin
                </a>
              </article>
            </div>
          </section>
        </main>
        <HomepageV3Footer />
        <AnalyticsConsentCard />
      </div>
    )
  }

  const snapshot = confirmation.snapshot
  const method = snapshot.method
  const orderId = snapshot.orderId
  const amountCents = snapshot.amountCents
  const isBankTransfer = method === 'bank-transfer'
  const placed = formatPlacedAt(snapshot.placedAt)

  return (
    <div className="sg-v3 checkout-confirmation-page">
      <a className="skip-link" href="#main-content">Skip to main content</a>
      <HomepageV3Header cartCount={0} activeHref="/checkout" />
      <main id="main-content" tabIndex={-1}>
        <section className={`checkout-confirmation checkout-confirmation--${isBankTransfer ? 'bank' : 'card'}`}>
          <div className="checkout-confirmation__layout">
            <article className="checkout-confirmation__primary">
              <header className="checkout-confirmation__hero">
                <span className="checkout-confirmation__hero-icon"><Icon name={isBankTransfer ? 'hourglass' : 'check'} /></span>
                <h1>{isBankTransfer ? 'Order Received!' : 'Order Confirmed!'}</h1>
                <p>
                  {isBankTransfer
                    ? 'Thanks for your order. Use the bank transfer details below and your exact Sugar Gremlin reference so we can match your payment.'
                    : 'Thank you for your order. We can’t wait to bake this for you!'}
                </p>
              </header>

              <div className="checkout-confirmation__meta-band">
                <div>
                  <span className="checkout-confirmation__meta-icon"><Icon name="receipt" /></span>
                  <p><strong>Order number</strong><b>{orderId}</b></p>
                </div>
                <div>
                  <span className="checkout-confirmation__meta-icon"><Icon name="calendar" /></span>
                  <p><strong>Order placed</strong>{placed.date}<small>{placed.time}</small></p>
                </div>
              </div>

              {isBankTransfer && orderId && amountCents !== null ? (
                <section className="checkout-confirmation__bank" aria-labelledby="bank-transfer-details-title">
                  <div className="checkout-confirmation__section-heading">
                    <span><Icon name="bank" /></span>
                    <div>
                      <h2 id="bank-transfer-details-title">Bank Transfer Details</h2>
                      <p>Please use the details below and your unique reference.</p>
                    </div>
                  </div>
                  <dl className="checkout-confirmation__bank-grid">
                    <div><dt>Account name</dt><dd><strong>{businessData.payment.bankTransfer.accountName}</strong><CopyButton value={businessData.payment.bankTransfer.accountName} label="account name" /></dd></div>
                    <div><dt>Account number</dt><dd><strong>{businessData.payment.bankTransfer.accountNumber}</strong><CopyButton value={businessData.payment.bankTransfer.accountNumber} label="account number" /></dd></div>
                    <div><dt>Amount to pay</dt><dd><strong>{formatCents(amountCents)}</strong></dd></div>
                    <div><dt>Reference</dt><dd><strong className="checkout-confirmation__reference">{orderId}</strong><CopyButton value={orderId} label="reference" /></dd></div>
                  </dl>
                  <div className="checkout-confirmation__important">
                    <span><Icon name="alert" /></span>
                    <div>
                      <strong>Important</strong>
                      <ul>
                        <li>Use your exact payment reference: <b>{orderId}</b></li>
                        <li>Pay the exact amount: <b>{formatCents(amountCents)}</b></li>
                        <li>Please make the bank transfer within 2 hours.</li>
                        <li>Your order is not marked paid until the bank transfer is received and confirmed.</li>
                      </ul>
                    </div>
                  </div>
                </section>
              ) : null}

              <div className="checkout-confirmation__progress" aria-label="Order progress">
                <ProgressStep icon="receipt" title="Order received" body="We’ve got your order" />
                <ProgressStep
                  icon={isBankTransfer ? 'hourglass' : 'chef'}
                  title={isBankTransfer ? 'Waiting for payment' : 'Baking your box'}
                  body={isBankTransfer ? 'We’ll confirm your transfer' : 'Fresh-baked to order'}
                />
                <ProgressStep
                  icon={isBankTransfer ? 'chef' : 'truck'}
                  title={isBankTransfer ? 'Baking your box' : 'Out for delivery'}
                  body={isBankTransfer ? 'Once payment is confirmed' : 'On your selected day'}
                />
              </div>

              <div className="checkout-confirmation__next">
                <span><Icon name="heart" /></span>
                <p>
                  <strong>What happens next?</strong>
                  {isBankTransfer
                    ? 'Once we receive and confirm your payment, we’ll mark your order paid and start baking your box fresh for your selected delivery day.'
                    : 'We’ll bake your box fresh for your selected delivery day. If you have any questions about your order, please get in touch.'}
                </p>
              </div>

              <a className="checkout-confirmation__home" href="/">
                <Icon name="home" />
                Back to Home
              </a>
            </article>

            <OrderPanel snapshot={snapshot} totalCents={amountCents} method={method} />
          </div>
        </section>
      </main>
      <HomepageV3Footer />
      <AnalyticsConsentCard />
    </div>
  )
}


function VolleyConfirmation() {
  const [status, setStatus] = useState<VolleyStatus | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  async function refresh(paymentId?: string) {
    setBusy(true)
    try { setStatus(await fetchVolleyStatus(paymentId)); setError('') }
    catch { setError("We couldn't check your payment just now. Please contact us before trying another payment.") }
    finally { setBusy(false) }
  }
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const payment = params.get('payment_id') ?? undefined
    window.history.replaceState({}, '', '/checkout/confirmation?method=volley')
    void refresh(payment)
  }, [])
  // Hosted TEST callbacks can arrive after the browser return and wait for cron.
  // Status reads share the server's bounded admission/backoff; never recheck orders.
  const waitingForTest = status?.testMode === true && status.testEvidence !== 'successful'
    && ['preparing','creating','creation_unknown','pending','unconfirmed','payment_identity_missing'].includes(status.state)
  useEffect(() => {
    if (!waitingForTest) return
    const timer = window.setInterval(() => { void refresh() }, 30_000)
    return () => window.clearInterval(timer)
  }, [waitingForTest])
  const confirmed = status?.testMode !== true && status?.confirmed === true && status.state === 'complete' && Boolean(status.snapshot && status.receipt)
  useEffect(() => { if (confirmed) { finishVolleySession(); clearCartBoxes(); clearCartAddOns(); clearOrderBuilderState(); clearCheckoutDraft() } }, [confirmed])
  return <VolleyConfirmationView status={status} error={error} busy={busy} onRefresh={() => void refresh()} />
}

export function VolleyConfirmationView({status,error='',busy=false,onRefresh}:{status:VolleyStatus|null;error?:string;busy?:boolean;onRefresh:()=>void}) {
  const confirmed = status?.testMode !== true && status?.confirmed === true && status.state === 'complete' && Boolean(status.snapshot && status.receipt)
  const needsHelp = status?.state === 'paid_needs_fulfilment'
  const unsuccessful = status?.state === 'unsuccessful'
  const testMode = status?.testMode === true
  const testSucceeded = testMode && status?.testEvidence === 'successful'
  const heading = testMode ? 'TEST MODE — no order placed' : confirmed ? 'Order Confirmed!' : needsHelp ? 'Let’s sort out your order' : unsuccessful ? 'Payment not confirmed' : 'We’re checking your payment'
  const message = testMode
    ? `${testSucceeded ? 'Your Volley TEST payment succeeded.' : status?.state === 'verification_failed' || status?.state === 'reconciliation_exhausted' ? 'This TEST payment needs review. Successful payment evidence has not been established.' : unsuccessful ? 'Volley did not confirm a successful TEST payment.' : 'This staging test payment is still awaiting verified successful provider evidence.'} No Sugar Gremlin order, delivery or confirmation email will be created.`
    : confirmed
    ? 'Thank you for your order. We can’t wait to bake this for you!'
    : needsHelp
      ? 'Your payment has been received, but we need to check your order before confirming it. Please don’t pay again. We’ll contact you to help with the next steps.'
      : unsuccessful
        ? 'Your order isn’t confirmed. Please contact us before trying another payment so we can check what happened.'
        : 'Your order isn’t confirmed yet. Please don’t try paying again. We’ll contact you if we need anything.'
  return <div className="sg-v3 checkout-confirmation-page">
    <a className="skip-link" href="#main-content">Skip to main content</a>
    <HomepageV3Header cartCount={0} activeHref="/checkout" />
    <main id="main-content" tabIndex={-1}>
      <section className={`checkout-confirmation checkout-confirmation--card${confirmed ? '' : ' checkout-confirmation--waiting'}`}>
        <div className="checkout-confirmation__layout">
          <article className="checkout-confirmation__primary" aria-busy={busy}>
            <header className="checkout-confirmation__hero">
              <span className="checkout-confirmation__hero-icon"><Icon name={confirmed || testSucceeded ? 'check' : unsuccessful || needsHelp ? 'alert' : 'hourglass'} /></span>
              <h1>{heading}</h1><p>{message}</p>
            </header>
            {confirmed && status ? <>
              <div className="checkout-confirmation__meta-band">
                <div><span className="checkout-confirmation__meta-icon"><Icon name="receipt" /></span><p><strong>Order number</strong><b>{status.orderId}</b></p></div>
                <div><span className="checkout-confirmation__meta-icon"><Icon name="check" /></span><p><strong>Payment received</strong><b>{formatCents(status.amountCents)}</b><small>Pay via Volley</small></p></div>
              </div>
              <div className="checkout-confirmation__progress" aria-label="Order progress">
                <ProgressStep icon="receipt" title="Order confirmed" body="Your payment is verified" />
                <ProgressStep icon="chef" title="Baking your box" body="Fresh-baked to order" />
                <ProgressStep icon="truck" title="Delivery" body="On your selected day" />
              </div>
            </> : null}
            <div className="checkout-confirmation__next">
              <span><Icon name="heart" /></span><p><strong>{confirmed ? 'What happens next?' : 'Need a hand?'}</strong>
                {testMode ? 'The owner can inspect this test record in Boss Zone. Do not make another payment to resolve this test record.' : confirmed ? 'We’ll bake your box fresh for your selected delivery day. If you have any questions about your order, please get in touch.' : 'Our team can help check your Pay via Volley payment. You can refresh this page for an update without making another payment.'}</p>
            </div>
            {error ? <p role="alert">{error}</p> : null}
            {!confirmed && !testSucceeded ? <button className="checkout-confirmation__home checkout-confirmation__refresh" type="button" onClick={onRefresh} disabled={busy}><Icon name="receipt" />{busy ? 'Checking…' : 'Refresh payment update'}</button> : null}
            <a className="checkout-confirmation__home" href="/contact"><Icon name="heart" />Contact Sugar Gremlin</a>
            {confirmed ? <a className="checkout-confirmation__home" href="/"><Icon name="home" />Back to Home</a> : null}
          </article>
          {confirmed && status?.snapshot && status.receipt ? <OrderPanel snapshot={{ ...status.snapshot, ...status.receipt }} totalCents={status.amountCents} method="volley" /> : null}
        </div>
      </section>
    </main><HomepageV3Footer /><AnalyticsConsentCard />
  </div>
}
export default function CheckoutConfirmationPage() {
  return new URLSearchParams(window.location.search).get('method') === 'volley' ? <VolleyConfirmation /> : <LegacyCheckoutConfirmationPage />
}
